import { INCOTERMS, type Incoterm, ORDER_STATUSES, type OrderStatus } from '@ambernord/shared';
import { Badge, Button, Card, CardBody, CardHeader, Field, Select, useToast } from '@ambernord/ui';
import { CheckCircle2, Truck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { formatDateTime } from '../../lib/format.js';
import type { ActorKind, SupplyOrderDto } from './types.js';

interface CarrierProfileLite {
  id: string;
  tenantId: string;
  displayName?: string | null;
  active?: boolean;
}

/** Platforma norīko tikai FCA variantus (DAP/DDP norīko piegādātājs — services/carriers.ts §3). */
const PLATFORM_INCOTERMS: readonly Incoterm[] = INCOTERMS.filter((i) => i.startsWith('FCA'));

const TERMINAL: readonly OrderStatus[] = ['paid', 'cancelled'];

function statusIndex(status: OrderStatus): number {
  return ORDER_STATUSES.indexOf(status);
}

/** Pārmaiņas atļautas tikai līdz iekraušanai — pēc `loaded` pārvadātājs ir fiksēts. */
function canChangeCarrier(status: OrderStatus): boolean {
  return !TERMINAL.includes(status) && statusIndex(status) < statusIndex('loaded');
}

/**
 * Pārvadātāja panelis (Night-3):
 * - platforma: norīko/pārnorīko pārvadātāju (POST /api/carriers/assign) + redz apstiprinājumu;
 * - pārvadātājs: apstiprina dispečēšanu (POST /api/carriers/:soId/confirm-dispatch);
 * - piegādātājs: tikai lasa pārvadātāja nosaukumu + apstiprinājuma stāvokli.
 * §7: rāda tikai pārvadātāja profila displayName — nekādu gala klienta datu.
 */
export function CarrierPanel({
  so,
  mine,
  onChanged,
}: {
  so: SupplyOrderDto;
  mine: ActorKind;
  onChanged: () => void | Promise<void>;
}) {
  const { t, i18n } = useTranslation('sodetail');
  const { toast } = useToast();
  const lang = i18n.language;
  const isPlatform = mine === 'platform';

  const [carriers, setCarriers] = useState<CarrierProfileLite[]>([]);
  const [selected, setSelected] = useState('');
  const [incoterm, setIncoterm] = useState<Incoterm>('FCA');
  const [formError, setFormError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sessionConfirmedAt, setSessionConfirmedAt] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const res = await api.get<{ items?: CarrierProfileLite[] }>('/api/carriers');
      if (res.ok) setCarriers(res.data.items ?? []);
    })();
  }, []);

  const current = useMemo(
    () => carriers.find((c) => c.tenantId === so.carrierTenantId),
    [carriers, so.carrierTenantId],
  );
  const activeCarriers = useMemo(() => carriers.filter((c) => c.active !== false), [carriers]);

  const confirmedAt = so.carrierConfirmedAt ?? sessionConfirmedAt;
  const confirmed = Boolean(confirmedAt);
  const changeable = canChangeCarrier(so.status);
  const showForm = isPlatform && changeable && (!so.carrierTenantId || editing);

  async function assign() {
    setFormError(null);
    if (!selected) {
      setFormError(t('carrier.missing'));
      return;
    }
    setBusy(true);
    try {
      const res = await api.post<{ error?: string }>('/api/carriers/assign', {
        supplyOrderId: so.id,
        carrierTenantId: selected,
        incoterm,
      });
      if (res.ok) {
        toast(t('carrier.assigned'));
        setEditing(false);
        setSelected('');
        await onChanged();
      } else {
        toast(res.data.error ?? t('carrier.error'), 'error');
      }
    } finally {
      setBusy(false);
    }
  }

  async function confirmDispatch() {
    setBusy(true);
    try {
      const res = await api.post<{ confirmedAt?: string; error?: string }>(
        `/api/carriers/${so.id}/confirm-dispatch`,
      );
      if (res.ok) {
        setSessionConfirmedAt(res.data.confirmedAt ?? new Date().toISOString());
        toast(t('carrier.dispatchSuccess'));
        await onChanged();
      } else {
        toast(res.data.error ?? t('carrier.error'), 'error');
      }
    } finally {
      setBusy(false);
    }
  }

  const carrierLabel = (c: CarrierProfileLite) => c.displayName || c.tenantId;

  return (
    <Card>
      <CardHeader title={t('carrier.title')} subtitle={t('carrier.hint')} />
      <CardBody className="space-y-3">
        <div>
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('carrier.current')}</p>
          <p
            className="flex items-center gap-1.5 text-sm font-medium text-slate-900 dark:text-white"
            data-testid="carrier-name"
          >
            <Truck className="h-4 w-4 text-slate-400" aria-hidden />
            {so.carrierTenantId
              ? current
                ? carrierLabel(current)
                : t('carrier.unknown')
              : t('carrier.none')}
          </p>
          {isPlatform && so.carrierAssignedAt ? (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('carrier.assignedAt', { at: formatDateTime(so.carrierAssignedAt, lang) })}
            </p>
          ) : null}
        </div>

        {so.carrierTenantId ? (
          confirmed ? (
            <Badge tone="green">
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
              {t('carrier.confirmedAt', { at: formatDateTime(confirmedAt, lang) })}
            </Badge>
          ) : mine === 'carrier' ? (
            !TERMINAL.includes(so.status) ? (
              <Button className="w-full" disabled={busy} onClick={() => void confirmDispatch()}>
                {t('carrier.confirmDispatch')}
              </Button>
            ) : null
          ) : (
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('carrier.pending')}</p>
          )
        ) : null}

        {showForm ? (
          <div className="space-y-3 border-t border-slate-200 pt-3 dark:border-slate-700">
            <Field label={t('carrier.select')} htmlFor="carrier-select" error={formError}>
              <Select
                id="carrier-select"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
              >
                <option value="">{t('carrier.selectPlaceholder')}</option>
                {activeCarriers.map((c) => (
                  <option key={c.id} value={c.tenantId}>
                    {carrierLabel(c)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('carrier.incoterm')} htmlFor="carrier-incoterm">
              <Select
                id="carrier-incoterm"
                value={incoterm}
                onChange={(e) => setIncoterm(e.target.value as Incoterm)}
              >
                {PLATFORM_INCOTERMS.map((i) => (
                  <option key={i} value={i}>
                    {t(`carrier.incoterms.${i}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <div className="flex justify-end gap-2">
              {editing ? (
                <Button variant="ghost" disabled={busy} onClick={() => setEditing(false)}>
                  {t('carrier.cancel')}
                </Button>
              ) : null}
              <Button disabled={busy} onClick={() => void assign()}>
                {t('carrier.assign')}
              </Button>
            </div>
          </div>
        ) : isPlatform && so.carrierTenantId ? (
          changeable ? (
            <Button variant="secondary" disabled={busy} onClick={() => setEditing(true)}>
              {t('carrier.reassign')}
            </Button>
          ) : (
            <p className="text-xs text-slate-500 dark:text-slate-400">{t('carrier.locked')}</p>
          )
        ) : null}
      </CardBody>
    </Card>
  );
}
